import React, { useMemo, useState } from 'react';
import {
  EligibilityAnswers,
  EligibilityFlag,
  SCREENING_QUESTIONS,
} from '../../services/eligibilityService';

/**
 * The eligibility questions, asked before payment.
 *
 * §5.1(6) requires the applicant to be "told, before paying, that the Service is
 * not urgent care, does not prescribe, and does not replace their general
 * practitioner", and §6.3 says an applicant with red flags "is not enrolled until
 * the acute problem is resolved". Taking the money first and screening afterwards
 * gets that order the wrong way round.
 *
 * TONE. §6.12 lists what is explicitly NOT a ground for exclusion — a protected
 * characteristic, a disability, low digital confidence, needing an interpreter, an
 * existing diagnosed condition — so this must not read as a list of ways to be
 * turned away. Every question says why it is being asked, and the two about access
 * exist to trigger §8's adjustments rather than to filter anyone out.
 *
 * WHAT IT DOES NOT DO. It does not decline anyone for a clinical reason. §7.2
 * gives that to a clinician and §4.3 withholds it even from a care clinician, so
 * a disclosure here holds enrolment for review rather than ending it. The one
 * exception is age, which is a fact rather than a judgement (§6.2).
 *
 * The crisis question is the one that cannot wait for any of that: it signposts
 * the moment it is answered, because §6.8's "same-day escalation" is not the same
 * as a same-day reply and the person is here now.
 */

const CRISIS_HELP = [
  'If you are in immediate danger, or you have harmed yourself, call 999 or go to your nearest A&E.',
  'Samaritans are free to call on 116 123, any time, day or night. You can also email jo@samaritans.org.',
  'You can text SHOUT to 85258 to talk to someone by text.',
  'NHS 111 can help you find urgent mental health support in your area.',
];

const URGENT_HELP = [
  'If this is an emergency, call 999 or go to your nearest A&E.',
  'For urgent advice that is not an emergency, call NHS 111 — free, 24 hours a day.',
  'Your GP can help with anything that can wait until they are open.',
];

function YesNo({
  value, onChange, name,
}: {
  value: boolean | null;
  onChange: (next: boolean) => void;
  name: string;
}) {
  return (
    <div className="flex gap-2 shrink-0">
      {[true, false].map((option) => (
        <button
          key={String(option)}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={value === option}
          className="px-4 py-1.5 rounded-lg text-sm border transition-colors"
          style={value === option
            ? { backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)', borderColor: 'var(--melio-charcoal)' }
            : { borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
        >
          {option ? 'Yes' : 'No'}
        </button>
      ))}
      <span className="sr-only">{name}</span>
    </div>
  );
}

function Signpost({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div
      role="alert"
      className="rounded-xl border px-4 py-3 mt-3"
      style={{ borderColor: 'rgba(139, 61, 43,0.45)', backgroundColor: 'rgba(139, 61, 43,0.06)' }}
    >
      <p className="text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>{title}</p>
      <ul className="text-sm space-y-1" style={{ color: 'var(--melio-charcoal)' }}>
        {lines.map((line) => <li key={line}>{line}</li>)}
      </ul>
    </div>
  );
}

export default function EligibilityScreening({
  dateOfBirth,
  onDateOfBirth,
  answers,
  onAnswers,
  blockers,
  flags,
}: {
  dateOfBirth: string;
  onDateOfBirth: (next: string) => void;
  answers: Partial<EligibilityAnswers>;
  onAnswers: (next: Partial<EligibilityAnswers>) => void;
  /** Set after the server has screened — an objective failure (§6.2, §5.1(2)). */
  blockers: EligibilityFlag[];
  /** Set after the server has screened — held for a clinician (§7.2). */
  flags: EligibilityFlag[];
}) {
  const [showCrisis, setShowCrisis] = useState(false);
  const [showUrgent, setShowUrgent] = useState(false);

  const set = (key: keyof EligibilityAnswers, value: boolean) => {
    onAnswers({ ...answers, [key]: value });
    // Signposted the instant it is disclosed, not after a round trip. A person who
    // has just said they are in crisis must not be looking at a spinner.
    if (key === 'mentalHealthCrisis' && value === true) setShowCrisis(true);
    if (key === 'urgentConcern' && value === true) setShowUrgent(true);
  };

  const answered = useMemo(
    () => SCREENING_QUESTIONS.every((q) => typeof answers[q.key] === 'boolean'),
    [answers],
  );

  return (
    <section>
      <p className="gh-eyebrow-ash" style={{ marginBottom: 8 }}>A few questions first</p>
      <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
        Melio is a preventive service for adults in the UK. It is not urgent care, it does not
        prescribe, and it does not replace your GP. These questions make sure it is the right fit
        before you pay for anything — and none of them are a test you can fail quietly: if
        something needs a conversation, a clinician has it with you.
      </p>

      <label className="block mb-5">
        <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
          Date of birth
        </span>
        <input
          type="date"
          value={dateOfBirth}
          onChange={(e) => onDateOfBirth(e.target.value)}
          className="w-full rounded-xl border px-4 py-3 text-sm"
          style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
        />
        <span className="block text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
          Melio is for adults. You need to be 18 or over to join.
        </span>
      </label>

      <label className="block mb-6">
        <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
          Do you live in the United Kingdom?
        </span>
        <YesNo
          name="Do you live in the United Kingdom?"
          value={answers.ukResident ?? null}
          onChange={(v) => set('ukResident', v)}
        />
        <span className="block text-xs mt-1.5" style={{ color: 'var(--melio-text-muted)' }}>
          Melio relies on UK emergency services being able to reach you.
        </span>
      </label>

      <div className="space-y-5">
        {SCREENING_QUESTIONS.map((q) => (
          <div key={q.key}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                  {q.prompt}
                </p>
                {q.help && (
                  <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                    {q.help}
                  </p>
                )}
              </div>
              <YesNo
                name={q.prompt}
                value={(answers[q.key] as boolean | null) ?? null}
                onChange={(v) => set(q.key, v)}
              />
            </div>

            {q.key === 'mentalHealthCrisis' && showCrisis && (
              <Signpost
                title="Please do not wait for Melio — help is available right now."
                lines={CRISIS_HELP}
              />
            )}
            {q.key === 'urgentConcern' && showUrgent && (
              <Signpost
                title="Melio cannot help urgently. Please use one of these instead."
                lines={URGENT_HELP}
              />
            )}
          </div>
        ))}

        {answers.gpRegistered === true && (
          <label className="block">
            <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
              Your GP practice
            </span>
            <input
              type="text"
              value={answers.gpPracticeName || ''}
              onChange={(e) => onAnswers({ ...answers, gpPracticeName: e.target.value })}
              placeholder="Practice name and town"
              className="w-full rounded-xl border px-4 py-3 text-sm"
              style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
            />
            <span className="block text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
              So Melio can write to them, and reach them if you are ever unwell during an
              appointment.
            </span>
          </label>
        )}
      </div>

      {/* §6.2 / §5.1(2) — objective, and the only outcome a form may decide. */}
      {blockers.length > 0 && (
        <div
          className="rounded-xl border px-4 py-3 mt-6"
          style={{ borderColor: 'rgba(139, 61, 43,0.45)' }}
        >
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--melio-charcoal)' }}>
            Melio is not able to take you on at the moment.
          </p>
          {blockers.map((b) => (
            <p key={b.key} className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>
              {b.route}
            </p>
          ))}
        </div>
      )}

      {/* §7.2 — held, not declined. A clinician decides and tells them. */}
      {blockers.length === 0 && flags.length > 0 && (
        <div
          className="rounded-xl border px-4 py-3 mt-6"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'rgba(79, 93, 24,0.05)' }}
        >
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--melio-charcoal)' }}>
            One of our clinicians will speak to you before you join.
          </p>
          <p className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>
            Nothing you have said rules you out. It means a clinician rather than a form should
            decide whether Melio is the right fit, and they will talk it through with you. You have
            not been charged.
          </p>
        </div>
      )}

      {!answered && (
        <p className="text-xs mt-4" style={{ color: 'var(--melio-text-muted)' }}>
          Please answer each question so we know Melio is right for you.
        </p>
      )}
    </section>
  );
}
